import { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { PolarEmbedCheckout } from '@polar-sh/checkout/embed'
import { useAuthStore } from '../store/authStore'
import { useToastStore } from '../store/toastStore'
import { referralAPI } from '../api/client'
import { track } from '../utils/analytics'

// Polar 대시보드에서 상품 생성 후 .env에 추가
// VITE_POLAR_PRODUCT_MONTHLY=xxx
// VITE_POLAR_PRODUCT_SEMESTER=xxx
const PRODUCTS = {
  monthly: import.meta.env.VITE_POLAR_PRODUCT_MONTHLY,
  semester: import.meta.env.VITE_POLAR_PRODUCT_SEMESTER,
}

const POLAR_API = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/polar`

export default function Pricing() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(null)
  const { user, refreshAccess } = useAuthStore()
  const showToast = useToastStore(s => s.showToast)
  const success = searchParams.get('success') === 'true'

  // pricing 페이지 진입 트래킹
  useEffect(() => { track('pricing-viewed') }, [])

  // 결제 성공 후 구독 상태 갱신 (웹훅 도달 지연 대비 딜레이 polling)
  useEffect(() => {
    if (!success || !user?.userId) return
    track('checkout-completed')
    refreshAccess()
    const t1 = setTimeout(() => refreshAccess(), 3000)
    const t2 = setTimeout(() => refreshAccess(), 8000)
    return () => { clearTimeout(t1); clearTimeout(t2) }
  }, [success])

  // 리퍼럴 상태
  const [referral, setReferral] = useState(null)
  const [refLoading, setRefLoading] = useState(true)
  const [refCode, setRefCode] = useState('')
  const [refApplying, setRefApplying] = useState(false)
  const [refError, setRefError] = useState(null)
  const refInputRef = useRef(null)

  useEffect(() => {
    PolarEmbedCheckout.init()
  }, [])

  useEffect(() => {
    if (!user?.userId) { setRefLoading(false); return }
    Promise.all([
      referralAPI.getDashboard(user.userId).catch(() => null),
      referralAPI.getMyCode(user.userId).catch(() => null),
    ]).then(([dash, code]) => {
      if (dash?.success) {
        setReferral({
          ...dash.data,
          code: dash.data.code || code?.data?.code,
        })
      }
    }).finally(() => setRefLoading(false))
  }, [user?.userId])

  const hasAccess = referral?.accessUntil && new Date(referral.accessUntil) > new Date()
  const accessDays = hasAccess ? Math.ceil((new Date(referral.accessUntil) - new Date()) / (1000 * 60 * 60 * 24)) : 0
  const accessExpired = referral?.accessUntil && !hasAccess
  const hasUsedReferral = referral?.hasUsedReferral
  const slotsUsed = referral?.usedCount || 0
  const slotsMax = referral?.maxUses || 3
  const slotsRemaining = slotsMax - slotsUsed

  const handleRefApply = async () => {
    if (refCode.length !== 6) { setRefError('6자리 코드를 입력해주세요'); return }
    setRefApplying(true); setRefError(null)
    try {
      const res = await referralAPI.apply(user.userId, refCode)
      if (res.success) {
        track('referral-applied')
        showToast('1주 무료 체험이 시작됐어요!', 'success')
        // 상태 리로드
        const dash = await referralAPI.getDashboard(user.userId)
        if (dash?.success) setReferral({ ...dash.data, code: dash.data.code || referral?.code })
        setRefCode('')
      }
    } catch (err) {
      const errCode = err.response?.data?.error
      const msgs = { already_used: '이미 사용한 코드예요', invalid_code: '유효하지 않은 코드예요', own_code: '본인 코드는 사용할 수 없어요', code_exhausted: '사용 횟수가 초과된 코드예요' }
      setRefError(msgs[errCode] || '코드 적용에 실패했어요')
    } finally { setRefApplying(false) }
  }

  const copyMyCode = async () => {
    if (!referral?.code) return
    track('referral-shared', { method: 'copy' })
    try { await navigator.clipboard.writeText(referral.code); showToast('코드가 복사됐어요', 'success') }
    catch { showToast('복사에 실패했어요', 'error') }
  }

  const shareMyCode = async () => {
    if (!referral?.code) return
    const url = `https://tayobus.app/r/${referral.code}`
    if (navigator.share) {
      track('referral-shared', { method: 'share' })
      try { await navigator.share({ title: '타요버스 초대', text: `타요버스에서 내 코드 ${referral.code} 입력하면 1주 무료!`, url }) } catch {}
    } else {
      track('referral-shared', { method: 'copy' })
      try { await navigator.clipboard.writeText(url); showToast('링크가 복사됐어요', 'success') }
      catch { showToast('복사에 실패했어요', 'error') }
    }
  }

  const handleCheckout = async (productKey) => {
    const productId = PRODUCTS[productKey]
    if (!productId) {
      alert('아직 준비 중인 요금제입니다.')
      return
    }

    track('checkout-started', { plan: productKey })
    setLoading(productKey)
    try {
      const studentId = user?.studentId || ''
      const res = await fetch(`${POLAR_API}/checkout?products=${productId}&studentId=${studentId}`)
      if (!res.ok) throw new Error('결제 세션 생성 실패')
      const { url, error } = await res.json()
      if (error) throw new Error(error)

      const checkout = await PolarEmbedCheckout.create(url, { theme: 'light' })
      checkout.addEventListener('success', () => {
        navigate('/pricing?success=true')
      })
    } catch (err) {
      track('checkout-error', { plan: productKey })
      console.error('[Pricing] checkout error:', err)
      alert('결제 창을 열 수 없습니다. 잠시 후 다시 시도해주세요.')
    } finally {
      setLoading(null)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b px-4 py-4 flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="p-2 -ml-2 rounded-full hover:bg-gray-100"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="text-lg font-bold">요금제</h1>
      </div>

      <div className="flex-1 px-4 py-6 max-w-md mx-auto w-full">
        {success && (
          <div className="mb-5 p-4 bg-green-50 border border-green-200 rounded-xl text-green-800 text-sm text-center font-medium">
            결제가 완료되었습니다!
          </div>
        )}

        {/* 리퍼럴 통합 섹션 */}
        {user && !refLoading && (
          <div className="mb-5 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            {/* 케이스 1: 코드 미사용 — 입력 UI */}
            {!hasUsedReferral && !hasAccess && (
              <div className="p-5">
                <div className="flex items-center gap-2.5 mb-3">
                  <div className="w-9 h-9 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-[18px] h-[18px] text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">초대 코드가 있으신가요?</h3>
                    <p className="text-xs text-gray-400">코드 입력하면 <span className="text-blue-500 font-semibold">1주 무료 체험!</span></p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <input
                    ref={refInputRef}
                    type="text"
                    inputMode="text"
                    autoCapitalize="characters"
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                    value={refCode}
                    onChange={(e) => { setRefCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6)); setRefError(null) }}
                    onKeyDown={(e) => e.key === 'Enter' && refCode.length === 6 && handleRefApply()}
                    placeholder="6자리 코드"
                    maxLength={6}
                    className={`flex-1 text-center text-lg font-bold tracking-[0.2em] pl-[0.2em] py-2.5 border-2 rounded-xl transition-colors outline-none ${
                      refError ? 'border-red-300 bg-red-50/50' : 'border-gray-200 bg-gray-50 focus:border-blue-400'
                    }`}
                  />
                  <button
                    onClick={handleRefApply}
                    disabled={refCode.length !== 6 || refApplying}
                    className="px-5 py-2.5 bg-blue-500 text-white font-semibold rounded-xl disabled:opacity-30 transition-all active:bg-blue-600 flex-shrink-0"
                  >
                    {refApplying ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : '적용'}
                  </button>
                </div>
                {refError && (
                  <p className="text-xs text-red-500 mt-2 text-center font-medium">{refError}</p>
                )}
              </div>
            )}

            {/* 케이스 2a: 구독 중 */}
            {hasAccess && referral?.subscriptionStatus === 'active' && (
              <div className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 bg-emerald-50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-[18px] h-[18px] text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-gray-900">구독 중</h3>
                      <p className="text-xs text-gray-400">
                        {new Date(referral.accessUntil).toLocaleDateString('ko-KR', { month: 'long', day: 'numeric' })}까지
                      </p>
                    </div>
                  </div>
                  <div className="bg-emerald-50 px-3 py-1.5 rounded-full">
                    <span className="text-emerald-600 text-sm font-bold">{accessDays}일 남음</span>
                  </div>
                </div>
              </div>
            )}

            {/* 케이스 2b: 구독 취소됨 */}
            {hasAccess && referral?.subscriptionStatus === 'cancelled' && (
              <div className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 bg-amber-50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-[18px] h-[18px] text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-gray-900">구독 취소됨</h3>
                      <p className="text-xs text-gray-400">
                        {new Date(referral.accessUntil).toLocaleDateString('ko-KR', { month: 'long', day: 'numeric' })}까지 이용 가능
                      </p>
                    </div>
                  </div>
                  <div className="bg-amber-50 px-3 py-1.5 rounded-full">
                    <span className="text-amber-600 text-sm font-bold">{accessDays}일 남음</span>
                  </div>
                </div>
              </div>
            )}

            {/* 케이스 2c: 무료 체험 중 */}
            {hasAccess && referral?.subscriptionStatus !== 'active' && referral?.subscriptionStatus !== 'cancelled' && (
              <div className="p-5">
                <div className="flex items-center justify-between mb-3.5">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 bg-green-50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-[18px] h-[18px] text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-gray-900">무료 체험 중</h3>
                      <p className="text-xs text-gray-400">
                        {new Date(referral.accessUntil).toLocaleDateString('ko-KR', { month: 'long', day: 'numeric' })}까지
                      </p>
                    </div>
                  </div>
                  <div className="bg-green-50 px-3 py-1.5 rounded-full">
                    <span className="text-green-600 text-sm font-bold">{accessDays}일 남음</span>
                  </div>
                </div>
                {/* 구분선 */}
                <div className="border-t border-gray-100 my-3.5" />
                {/* 친구 초대로 연장 */}
                <p className="text-xs text-gray-500 mb-3">친구를 초대하면 이용 기간이 늘어나요</p>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-gray-50 rounded-xl px-4 py-2.5 text-center">
                    <span className="text-lg font-bold tracking-[0.2em] text-gray-900 pl-[0.2em] select-all">{referral.code}</span>
                  </div>
                  <button onClick={copyMyCode} className="p-2.5 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors" title="코드 복사">
                    <svg className="w-[18px] h-[18px] text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                  <button onClick={shareMyCode} className="p-2.5 bg-blue-500 rounded-xl hover:bg-blue-600 transition-colors" title="링크 공유">
                    <svg className="w-[18px] h-[18px] text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                  </button>
                </div>
                <div className="flex items-center gap-1.5 mt-3">
                  {Array.from({ length: slotsMax }).map((_, i) => (
                    <div key={i} className={`flex-1 h-1.5 rounded-full ${i < slotsUsed ? 'bg-blue-500' : 'bg-gray-200'}`} />
                  ))}
                  <span className="text-xs text-gray-400 ml-1 flex-shrink-0">{slotsUsed}/{slotsMax}</span>
                </div>
              </div>
            )}

            {/* 케이스 3: trial 만료 — 초대 유도 or 구독 유도 */}
            {accessExpired && (
              <div className="p-5">
                <div className="flex items-center gap-2.5 mb-3">
                  <div className="w-9 h-9 bg-amber-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-[18px] h-[18px] text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">타요패스가 만료됐어요</h3>
                    {slotsRemaining > 0 ? (
                      <p className="text-xs text-gray-400">친구를 초대하면 <span className="text-blue-500 font-semibold">다시 1주 무료!</span></p>
                    ) : (
                      <p className="text-xs text-gray-400">아래 요금제를 선택해주세요</p>
                    )}
                  </div>
                </div>
                {slotsRemaining > 0 && (
                  <>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-50 rounded-xl px-4 py-2.5 text-center">
                        <span className="text-lg font-bold tracking-[0.2em] text-gray-900 pl-[0.2em] select-all">{referral.code}</span>
                      </div>
                      <button onClick={copyMyCode} className="p-2.5 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors">
                        <svg className="w-[18px] h-[18px] text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                      <button onClick={shareMyCode} className="p-2.5 bg-blue-500 rounded-xl hover:bg-blue-600 transition-colors">
                        <svg className="w-[18px] h-[18px] text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                        </svg>
                      </button>
                    </div>
                    <div className="flex items-center gap-1.5 mt-3">
                      {Array.from({ length: slotsMax }).map((_, i) => (
                        <div key={i} className={`flex-1 h-1.5 rounded-full ${i < slotsUsed ? 'bg-blue-500' : 'bg-gray-200'}`} />
                      ))}
                      <span className="text-xs text-gray-400 ml-1 flex-shrink-0">{slotsUsed}/{slotsMax}</span>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        <p className="text-gray-500 text-sm text-center mb-6">
          자동 예약 서비스 이용을 위한 요금제를 선택해주세요.
        </p>

        <div className="space-y-4">
          {/* 월간 구독 */}
          <div className="bg-white rounded-2xl border-2 border-blue-500 p-5 shadow-sm">
            <div className="flex items-start justify-between mb-3">
              <div>
                <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                  추천
                </span>
                <h2 className="text-lg font-bold mt-2">월간 구독</h2>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold">$9.99</div>
                <div className="text-gray-400 text-sm">/ 월</div>
              </div>
            </div>
            <ul className="text-sm text-gray-600 space-y-1.5 mb-4">
              <li className="flex items-center gap-2">
                <span className="text-blue-500 font-bold">✓</span> 자동 버스 예약
              </li>
              <li className="flex items-center gap-2">
                <span className="text-blue-500 font-bold">✓</span> 예약 완료 푸시 알림
              </li>
              <li className="flex items-center gap-2">
                <span className="text-blue-500 font-bold">✓</span> 언제든 취소 가능
              </li>
            </ul>
            <button
              onClick={() => handleCheckout('monthly')}
              disabled={!!loading}
              className="w-full py-3 bg-blue-500 text-white font-semibold rounded-xl disabled:opacity-50 transition-colors active:bg-blue-600"
            >
              {loading === 'monthly' ? '처리 중...' : hasAccess ? `카드 등록 (${accessDays}일 후 결제)` : '월간 구독 시작'}
            </button>
          </div>

          {/* 학기 이용권 */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h2 className="text-lg font-bold">학기 이용권</h2>
                <div className="text-xs text-gray-400 mt-0.5">4개월 / 1회 결제</div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold">$29.99</div>
                <div className="text-xs text-green-600 font-semibold">월 대비 25% 절약</div>
              </div>
            </div>
            <ul className="text-sm text-gray-600 space-y-1.5 mb-4">
              <li className="flex items-center gap-2">
                <span className="text-green-500 font-bold">✓</span> 자동 버스 예약
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500 font-bold">✓</span> 예약 완료 푸시 알림
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500 font-bold">✓</span> 한 학기 무제한 사용
              </li>
            </ul>
            <button
              onClick={() => handleCheckout('semester')}
              disabled={!!loading}
              className="w-full py-3 bg-gray-900 text-white font-semibold rounded-xl disabled:opacity-50 transition-colors active:bg-gray-700"
            >
              {loading === 'semester' ? '처리 중...' : '학기 이용권 구매'}
            </button>
          </div>
        </div>

        {/* 구독 관리 버튼 (Polar 구독 유저만) */}
        {referral?.subscriptionStatus === 'active' && (
          <button
            onClick={async () => {
              try {
                const res = await fetch(
                  `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/polar/portal?userId=${user?.userId}`,
                  { headers: { 'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`, 'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY } }
                )
                const data = await res.json()
                if (data.url) window.open(data.url, '_blank')
                else showToast(data.error || '포탈을 열 수 없습니다', 'error')
              } catch { showToast('포탈을 열 수 없습니다', 'error') }
            }}
            className="w-full mt-4 py-3 text-sm font-medium text-gray-500 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
          >
            구독 관리 (결제 수단 변경 · 취소)
          </button>
        )}

        <p className="text-center text-xs text-gray-400 mt-4">
          결제는 Polar를 통해 안전하게 처리됩니다.
        </p>
      </div>
    </div>
  )
}
